import React, { useState } from "react";
import { Row, Col, Button, Input, Modal, Select, Form, Radio, Spin } from "antd";
import { LoadingOutlined, WarningOutlined, ReloadOutlined } from "@ant-design/icons";
import { Scrollbars } from "react-custom-scrollbars";
import axios from "axios";
import { completePayment } from "../../Core/payment/paymentController";
import { cancelPLBPolling, cancelPLBTransaction, recheckPLBTransactionStatus } from "../../Core/payment/paymentProvider";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useAuthStore from "../../../../stores/useAuthStore";
import { resolveManualTopupAmount } from "../../../../utils/topupPaymentAmount";

const { Option } = Select;

const CardPaymentModal = ({
  showCardPaymnteModal,
  setShowCardPaymnteModal,
  cardPaymnetError,
  setCardPaymnetError,
  cardPaymnetStatus,
  setCardPaymnetStatus,
  paymentType,
  setPaymentType,
  selectedPaymentMethod,
  amount,
  CardPaymentForm,
  integratedPaymentStatus,
  currenciesList,
  cart,
  setCart,
  posConfig,
  data,
  setInputFocused,
  handleCardPayment,
  requestPayment,
  processOrder,
  setAmount,
  setSelectedPaymentMethod,
  overPayedAmount,
  tillData,
  setIsCardPaymentFlag,
  showCustomerWallet,
  enterTotalQtyForWallet,
  isNewGiftCardFlag,
  showGiftCardTopup,
  giftCardTopupPaymentMethod,
  setGiftCardTopupPaymentMethod,
  giftCardTopupCardInput,
  handleGiftCardTopupPostPayment,
  handleWalletPostPayment,
  currentCardInput,
  setCurrentCardInput,
}) => {
  // BUG-1962: after a PLB timeout, let the cashier re-verify the SAME already-
  // submitted transaction (staff-initiated, no silent/automatic behavior change)
  // before deciding to retry or override.
  const showRecheckAction = cardPaymnetStatus?.title === "Payment Timed Out";
  // recheckPLBTransactionStatus has no internal re-entrancy guard (unlike
  // requestPayment's paymentInProgressRef), so a rapid double-click could fire two
  // concurrent checkStatus calls and, if both return approved, double-call
  // completePayment for the same transaction. disabled+loading below (same
  // pattern as every other async button in this app, e.g. LoyaltyOTPVerificationModal)
  // prevents that.
  const [checkingStatus, setCheckingStatus] = useState(false);
  // TSK-3841: guards the processing-view Cancel button the same way checkingStatus
  // guards Retry above — cancelPLBTransaction() makes a network call, so a rapid
  // double-click must not fire two concurrent cancelTransaction requests.
  const [cancelling, setCancelling] = useState(false);
  const recheckPaymentStatus = async () => {
    if (checkingStatus) return;
    setCheckingStatus(true);
    try {
      const wallet = showCustomerWallet || (isNewGiftCardFlag && showGiftCardTopup);
      await recheckPLBTransactionStatus(
        cart,
        setCart,
        selectedPaymentMethod,
        setAmount,
        setSelectedPaymentMethod,
        completePayment,
        setShowCardPaymnteModal,
        setCardPaymnetError,
        setCardPaymnetStatus,
        showCustomerWallet,
        handleWalletPostPayment,
        isNewGiftCardFlag,
        showGiftCardTopup,
        handleGiftCardTopupPostPayment,
        wallet
      );
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <Modal
      centered
      width={integratedPaymentStatus ? "35%" : "40%"}
      footer={null}
      styles={{ body: { padding: 0 } }}
      closable={false}
      className="actionsModal"
      open={showCardPaymnteModal}
      getContainer={false}
      zIndex={2000}
    >
      {cardPaymnetError ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            background: "#f3f4f9",
            borderRadius: "15px",
            padding: "6%",
          }}
        >
          <Scrollbars style={{ minHeight: "55vh" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: 500,
                    textAlign: "left",
                    margin: 0,
                  }}
                >
                  {cardPaymnetStatus.paymentMode} Payment
                </p>
                <p
                  style={{
                    paddingTop: "1.6vh",
                    fontSize: "1rem",
                    color: "#929098",
                    fontWeight: 400,
                    textAlign: "left",
                    margin: 0,
                  }}
                >
                  Process {cardPaymnetStatus.paymentMode} payment of {currenciesList[0]?.currSymbol} {amount}
                </p>
              </div>
              {showRecheckAction && (
                <Button
                  icon={<ReloadOutlined />}
                  loading={checkingStatus}
                  disabled={checkingStatus}
                  onClick={recheckPaymentStatus}
                  style={{
                    flexShrink: 0,
                    borderRadius: "20px",
                    borderColor: "#d9d9d9",
                    color: "#333",
                    fontWeight: 500,
                  }}
                >
                  Retry
                </Button>
              )}
            </div>
            <Radio.Group
              value={paymentType}
              onChange={(e) => {
                if (e.target.value === "edc" && !selectedPaymentMethod.integratedPayment) return;
                setCardPaymnetStatus({
                  paymentStatus: false,
                  message: "",
                  paymentMode: "",
                });
                setPaymentType(e.target.value);
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "2vh 0",
                gap: "16px",
                color: "#2F3856",
              }}
              disabled={cardPaymnetStatus.paymentStatus}
            >
              <Radio value="edc" style={{ fontSize: "1rem" }}>
                Process through EDC machine
              </Radio>
              <Radio value="manual" style={{ fontSize: "1rem" }}>
                Manual entry (Override)
              </Radio>
            </Radio.Group>

            {cardPaymnetStatus.message !== "" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid rgba(146, 144, 152, 0.5)",
                }}
              >
                <WarningOutlined
                  style={{
                    fontSize: "1rem",
                    marginRight: "12px",
                    marginTop: "2px",
                    color: "#555359",
                  }}
                />
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 500,
                      fontSize: "1rem",
                      color: "#0F0718",
                    }}
                  >
                    {cardPaymnetStatus.title || "Warning"}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.9rem",
                      color: "#0F0718",
                      fontWeight: 400,
                    }}
                  >
                    {cardPaymnetStatus.message}
                  </p>
                </div>
              </div>
            )}

            {paymentType === "manual" &&
              (() => {
                // Substring match — Tab7 used array.includes which required
                // exact "card"/"upi" name. Tenant methods like "Credit Card",
                // "Debit Card", "UPI Payment" all skipped the form. (Tab8 #13/#14)
                const sel = selectedPaymentMethod?.name?.toLowerCase() || "";
                const gct = giftCardTopupPaymentMethod?.name?.toLowerCase() || "";
                const matches = (n) => n.includes("card") || n.includes("upi");
                return matches(sel) || matches(gct);
              })() && (
                <Form layout="vertical" onFinish={handleCardPayment} form={CardPaymentForm}>
                  <Row>
                    <Col span={24}>
                      {posConfig?.transactionID === "Y" ? (
                        <Form.Item
                          style={{ marginBottom: "10px" }}
                          name="transactionId"
                          rules={[
                            {
                              required: posConfig?.transactionTime_mandatory === "Yes" ? true : false,
                              message: `Please enter Transaction ID`,
                            },
                          ]}
                          label={
                            <span
                              style={{
                                color: "#000",
                                fontSize: "1.2vw",
                                fontWeight: "500",
                              }}
                            >
                              Transaction ID
                            </span>
                          }
                        >
                          <Input
                            id="transactionId"
                            placeholder={`Transaction ID`}
                            // prefix={<UserOutlined style={{color:"#929098"}}/>}
                            onFocus={() => {
                              setInputFocused("transactionId");
                            }}
                            size="large"
                            onPressEnter={(e) => e.preventDefault()}
                            allowClear={false}
                            style={{
                              ...data.posScreenStyles.orderHistoryModal.cashManagmentSearchInput,
                              padding: "2vh 1vw",
                            }}
                          />
                        </Form.Item>
                      ) : (
                        ""
                      )}
                    </Col>
                    <Col span={24} style={{ padding: "" }}>
                      {posConfig?.authorizationCode === "Y" ? (
                        <Form.Item
                          style={{ marginBottom: "10px" }}
                          name="authorization"
                          rules={[
                            {
                              required: posConfig?.authorizationCode_mandatory === "Yes" ? true : false,
                              message: `Please enter Authorization Code`,
                            },
                          ]}
                          label={
                            <span
                              style={{
                                color: "#000",
                                fontSize: "1.2vw",
                                fontWeight: "500",
                              }}
                            >
                              Authorization Code
                            </span>
                          }
                        >
                          <Input
                            id="pin"
                            placeholder={`Authorization Code`}
                            size="large"
                            onPressEnter={(e) => e.preventDefault()}
                            onFocus={() => {
                              setInputFocused("authorization");
                            }}
                            allowClear={false}
                            style={{
                              ...data.posScreenStyles.orderHistoryModal.cashManagmentSearchInput,
                              padding: "2vh 1vw",
                            }}
                          />
                        </Form.Item>
                      ) : (
                        ""
                      )}
                    </Col>
                    <Col span={24}>
                      {posConfig?.cardType === "Y" ? (
                        <Form.Item
                          style={{ marginBottom: "10px" }}
                          name="cardType"
                          rules={[
                            {
                              required: posConfig?.cardType_mandatory === "Yes" ? true : false,
                              message: `Please enter card Type`,
                            },
                          ]}
                          label={
                            <span
                              style={{
                                color: "#000",
                                fontSize: "1.2vw",
                                fontWeight: "500",
                              }}
                            >
                              Card Type
                            </span>
                          }
                        >
                          <Select
                            className="selecItem"
                            getPopupContainer={(triggerNode) => triggerNode.parentNode}
                            placeholder={<span className="cartSelect">Card Type</span>}
                            size="large"
                          >
                            <Option className="cartOption" key="VI" value="VI">
                              Visa - VI
                            </Option>
                            <Option className="cartOption" key="MA" value="MA">
                              Master Card - MA
                            </Option>
                            <Option className="cartOption" key="RU" value="RU">
                              Rupay - RU
                            </Option>
                            <Option className="cartOption" key="AM" value="AM">
                              Amex - AM
                            </Option>
                            <Option className="cartOption" key="ME" value="ME">
                              Maestro - ME
                            </Option>
                          </Select>
                        </Form.Item>
                      ) : (
                        ""
                      )}
                    </Col>
                    <Col span={24}>
                      {posConfig?.CardNumber === "Y" ? (
                        <Form.Item
                          style={{ marginBottom: "10px" }}
                          name="cardNumber"
                          label={
                            <span
                              style={{
                                color: "#000",
                                fontSize: "1.2vw",
                                fontWeight: "500",
                              }}
                            >
                              Card Number(Last 4 Digits)
                            </span>
                          }
                        >
                          <Input
                            id="redeemNumber"
                            placeholder={`Enter Number`}
                            // prefix={<UserOutlined style={{color:"#929098"}}/>}
                            onFocus={() => {
                              setInputFocused("cardNumber");
                            }}
                            size="large"
                            onPressEnter={(e) => e.preventDefault()}
                            allowClear={false}
                            style={{
                              ...data.posScreenStyles.orderHistoryModal.cashManagmentSearchInput,
                              padding: "2vh 1vw",
                            }}
                          />
                        </Form.Item>
                      ) : (
                        ""
                      )}
                    </Col>
                  </Row>
                </Form>
              )}
          </Scrollbars>

          <div
            style={{
              display: "flex",
              gap: "16px",
              paddingTop: "3vh",
            }}
          >
            <Button
              size="large"
              style={{
                flex: 1,
                height: "9vh",
                fontSize: "1.2vw",
                borderRadius: "8px",
              }}
              onClick={async () => {
                setShowCardPaymnteModal(false);
                setCardPaymnetError(false);
                // TSK-3841: "Close" means abandon/stop watching entirely — unlike the
                // processing-view Cancel button below, which leaves polling running so
                // checkStatus can confirm the cancellation, Close stops polling and
                // clears state immediately. cancelPLBTransaction() reads localStorage
                // synchronously before its first await, so firing it before the
                // removeItem calls below still captures the right transaction. Passing
                // the current provider makes it a no-op for non-PLB flows (e.g. ADC),
                // handled separately below — see TIC-9452.
                cancelPLBTransaction(selectedPaymentMethod?.paymentProvider);
                cancelPLBPolling();
                localStorage.removeItem("transactionId");
                localStorage.removeItem("transactionAmount");
                localStorage.removeItem("plbOriginalRequest");
                if (!selectedPaymentMethod.integratedPayment) return;
                setPaymentType("edc");
                if (cardPaymnetStatus.paymentStatus) {
                  setCardPaymnetStatus({
                    paymentStatus: false,
                    message: "Payment processing failed. Please try again. If the problem persists, contact support.",
                  });
                  try {
                    // BUG-2175: axios.post(url, data, config) — headers belongs in the 3rd
                    // (config) argument, not the 2nd (request body). Passing it as the 2nd
                    // arg sent {"headers":{...}} as the literal POST body, with no
                    // transaction/ReceiptNo at all, so the terminal always rejected the
                    // cancel with "Reference number is required" and never actually
                    // cancelled the in-flight payment. Built to mirror the exact
                    // transaction/ReceiptNo shape the original /payment request already
                    // sends (paymentProvider.js requestPayment) so the terminal can match
                    // this cancel to the payment it's meant to cancel.
                    const hwBaseUrl = tillData.tillAccess?.cwrTill?.pHWController?.imageUrl ?? "";
                    const adcUrl = `${hwBaseUrl}cancel`;
                    const nameTill = useAuthStore.getState().tillValue?.name ?? "UnknownTill";
                    const cancelValues = {
                      type: 0,
                      terminal: nameTill,
                      transaction: `${tillData?.tillAccess?.cwrTill?.prefix ?? ""}${cart?.documentno ?? ""}`,
                      ReceiptNo: `${tillData?.tillAccess?.cwrTill?.prefix ?? ""}${cart?.documentno ?? ""}`,
                    };
                    const response = await axios.post(adcUrl, cancelValues, {
                      headers: {
                        "Content-Type": "application/xml; charset=UTF-8",
                        Accept: "application/xml",
                      },
                    });
                  } catch (err) {
                  }
                }
              }}
            >
              Close
            </Button>
            <Button
              type="primary"
              size="large"
              disabled={cardPaymnetStatus.paymentStatus}
              style={{
                flex: 1,
                height: "9vh",
                fontSize: "1.2vw",
                backgroundColor: "#2F3856",
                borderColor: "#2F3856",
                borderRadius: "8px",
                color: "#fff",
              }}
              onClick={() => {
                if (paymentType === "edc") {
                  setCardPaymnetError(false);
                  setPaymentType("edc");
                  setShowCardPaymnteModal(true);
                  const exchangeCart = useTransactionStore.getState().selectedReturnType === "EXCHANGE" ? useCartStore.getState().exchangeCart : null;
                  const effectiveTotal = exchangeCart ? parseFloat(Math.abs(cart.total)) + parseFloat(Math.abs(exchangeCart.total || 0)) : parseFloat(Math.abs(cart.total));
                  const effectivePaid = exchangeCart ? exchangeCart.paid || 0 : parseFloat(cart.paid || 0);
                  if (effectiveTotal > effectivePaid) {
                    requestPayment(selectedPaymentMethod, amount);
                  } else {
                    processOrder();
                  }
                } else if (paymentType === "manual") {
                  CardPaymentForm.validateFields()
                    .then((values) => {
                      if (showCustomerWallet || (isNewGiftCardFlag && showGiftCardTopup)) {
                        localStorage.setItem("cardPaymentDetails", JSON.stringify(values));
                      }
                      setCardPaymnetError(false);
                      setShowCardPaymnteModal(false);
                      setPaymentType("manual");
                      const selName = selectedPaymentMethod?.name?.toLowerCase() || "";
                      if (selName.includes("card") || selName.includes("upi")) {
                        cart.cardPaymentData = cart.cardPaymentData || { payload: [], response: [] };
                        cart.cardPaymentData.payload = [{ ...values }];
                        cart.cardPaymentData.response = [{ manual_card: "Y" }];
                        handleCardPayment(values);
                      }
                      let paymentMethod;
                      if (isNewGiftCardFlag && showGiftCardTopup) {
                        paymentMethod = giftCardTopupPaymentMethod;
                      } else {
                        paymentMethod = selectedPaymentMethod;
                      }
                      // BUG-2583: a top-up is completed with the amount of this attempt, whatever the
                      // method. The Card box is empty for UPI and gave amount NaN.
                      let inputAmount;
                      if (isNewGiftCardFlag && showGiftCardTopup) {
                        inputAmount = resolveManualTopupAmount({ giftCardTopup: true, paymentMethod, cardInput: giftCardTopupCardInput });
                      } else if (showCustomerWallet) {
                        inputAmount = resolveManualTopupAmount({ giftCardTopup: false, paymentMethod, cardInput: currentCardInput });
                      } else {
                        inputAmount = amount;
                      }
                      const wallet = showCustomerWallet || (isNewGiftCardFlag && showGiftCardTopup);
                      completePayment(
                        paymentMethod,
                        inputAmount,
                        cart,
                        setCart,
                        setAmount,
                        setSelectedPaymentMethod,
                        overPayedAmount,
                        null,
                        null,
                        wallet,
                        tillData.tillAccess.csBunit.currencies[0].prcPrecision,
                        null,
                        showCustomerWallet,
                        handleWalletPostPayment,
                        isNewGiftCardFlag,
                        showGiftCardTopup,
                        handleGiftCardTopupPostPayment
                      );
                    })
                    .catch((error) => {
                      console.error("Validation failed:", error);
                    });
                }
              }}
            >
              Process Payment
            </Button>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            height: "58vh",
            background: "#f3f4f9",
            borderRadius: "15px",
            padding: "6%",
          }}
        >
          <p
            style={{
              fontSize: "1.2rem",
              fontWeight: 500,
              textAlign: "left",
              width: "100%",
              margin: 0,
            }}
          >
            {cardPaymnetStatus.paymentMode} Payment
          </p>
          <p style={{ paddingTop: "1.6vh", fontSize: "1rem", color: "#929098", fontWeight: 400, textAlign: "left", width: "100%", margin: 0 }}>
            Process {cardPaymnetStatus.paymentMode} payment of {currenciesList[0]?.currSymbol}
            {showCustomerWallet
              ? (() => {
                  try {
                    const data = JSON.parse(localStorage.getItem("walletDataForCardIntegration"));
                    return data?.paymentAmount || 0;
                  } catch {
                    return amount;
                  }
                })()
              : isNewGiftCardFlag && showGiftCardTopup
                ? (() => {
                    try {
                      const data = JSON.parse(localStorage.getItem("giftCardIntegrationTopupData"));
                      return data?.paymentValue || 0;
                    } catch {
                      return amount;
                    }
                  })()
                : amount}
          </p>
          <Spin style={{ padding: "3vh 0" }} indicator={<LoadingOutlined style={{ fontSize: 45 }} spin />} spinning={true} />
          <p style={{ paddingTop: "3vh", fontSize: "1.2rem", color: "#0F0718", textAlign: "center", paddingBottom: "4vh" }}>Processing payment through EDC machine...</p>

          <Button
            style={{
              backgroundColor: "#2F3856",
              color: "#fff",
              fontSize: "1.2vw",
              width: "15vw",
              height: "9vh",
              borderRadius: "7px",
              padding: "10px 0",
            }}
            loading={cancelling}
            disabled={cancelling}
            onClick={async () => {
              if (cancelling) return;
              setCancelling(true);
              try {
                // TSK-3841: request cancellation of the Pine Labs transaction, but
                // deliberately do NOT stop polling here (no cancelPLBPolling call) —
                // CancelTransactionForced is a request, not a guaranteed instant
                // state change, so checkStatus (still running via
                // pollTransactionStatus) is left to confirm what actually happened
                // and will overwrite cardPaymnetStatus below once it gets a
                // definitive answer (cancelled / still approved / failed / timeout).
                const requested = await cancelPLBTransaction(selectedPaymentMethod?.paymentProvider);
                // The error/action view re-shows whatever is in cardPaymnetStatus.message
                // (the Warning box) — without this it kept showing the stale "Payment is
                // processing, Please wait" text from processPLBPayment even though the
                // toast above already fired. requested is undefined for any non-PLB
                // provider (cancelPLBTransaction's own no-op case), so those get generic
                // wording instead of Pine Labs-specific text — see TIC-9452.
                setCardPaymnetStatus({
                  paymentStatus: false,
                  message:
                    requested === false
                      ? "Could not reach Pine Labs to cancel. Please check the terminal."
                      : requested === true
                        ? "Cancelling transaction. Confirming with Pine Labs..."
                        : "Payment cancelled.",
                  paymentMode: selectedPaymentMethod?.name,
                });
              } finally {
                setCancelling(false);
                setCardPaymnetError(true);
              }
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default CardPaymentModal;
